import { RouterState } from 'connected-react-router';
import { IntegrationsState } from 'redux/integrations/reducers';
import { Aircraft } from '../common/types/aircraft';
import { Company } from '../common/types/Company';
import { Pilot } from '../common/types/pilot';
import { CommonInitState } from './common/reducers';
import { IntegrationCompanyConfig } from '../common/types/IntegrationCompanyConfig';

export const getUser = ({ user }) => user;

export const getUserRole = ({ user: { CompanyUsers = [] } }) => {
  const roles = CompanyUsers?.[0]?.Roles || [];

  if (roles.includes('adminManager') && !roles.includes('admin')) {
    return [...roles, 'admin'];
  }

  return roles;
};

// Admin managers get admin-level access to everything except managing company users: they can edit
// user details but cannot add users, deactivate them, or change their admin roles
export const getIsRestrictedAdminManager = ({ user }) => {
  const roles = user?.CompanyUsers?.[0]?.Roles || [];

  return (
    Boolean(user?.Company?.Settings?.AdminManagerUserRole) &&
    roles.includes('adminManager') &&
    !roles.includes('admin') &&
    !roles.includes('owner')
  );
};

export const getUserReviewerTypes = ({ user: { CompanyUsers = [] } }) =>
  CompanyUsers?.[0]?.ReviewerType || [];

export const getSettings = ({ settings }) => settings;

export const getCompanyID = ({ user: { CompanyUsers = [] } }) =>
  CompanyUsers.map(x => x.CompanyID).find(x => x);

export const getCompany = ({ user: { Company } }) => Company as Company;
export const getCompanyIntegrationConfigs = ({ user: { IntegrationCompanyConfigs } }) =>
  IntegrationCompanyConfigs as IntegrationCompanyConfig[];

export const getCompanySettings = ({ user: { Company } }) =>
  Company.Settings as Company['Settings'];

// Resolves the company's configured measurement units for the dashboard,
// falling back to imperial defaults when the setting is not set in the DB.
export const getMeasurementUnits = ({ user: { Company } }) => {
  const units = Company?.Settings?.CompanyMeasurementUnits;
  return {
    weight: units?.WeightUnit || 'lb',
    length: units?.LengthUnit || 'in',
  };
};

export const getMenu = ({ menu }) => menu;

export const getAerochexEnabled = ({
  user: {
    Company: { AerochexEnabled },
  },
}) => AerochexEnabled;

export const getCampEnabled = ({
  integrations: { campConfig },
}: {
  integrations: IntegrationsState;
}) => Boolean(campConfig);

export const getAirportsPage = ({ pages: { airports } }) => airports;

export const getAircraftPage = ({ pages: { aircraft } }) => aircraft;

export const getAircraftDetailsPage = ({ pages: { aircraftDetails } }) =>
  aircraftDetails as { aircraft: Aircraft };

export const getAircraftLogDetailsPage = ({ pages: { aircraftLogDetails } }) => aircraftLogDetails;

export const getCompanyUsersPage = ({ pages: { users } }) => users;

export const getCompanyFlightsPage = ({ pages: { companyFlights } }) => companyFlights;

export const getAerochexPage = ({ pages: { aerochex } }) => aerochex;

export const getCrewSchedulingV2Page = ({ pages: { crewSchedulingV2 } }) => crewSchedulingV2;

export const getCrewSchedulingV2Pilots = ({ pages: { crewSchedulingV2 } }) =>
  crewSchedulingV2.pilots as Pilot[];

export const getCrewSchedulingV2AircraftIdentifiers = ({ pages: { crewSchedulingV2 } }) =>
  crewSchedulingV2.selectedAircraftIdentifiers as string[];

export const getDutyScheduleEntryModal = ({ pages: { dutyScheduleModal } }) => dutyScheduleModal;

export const getScheduleEntryModal = ({ pages: { scheduleEntryModal } }) => scheduleEntryModal;

export const getMaintenanceEntryModal = ({ pages: { maintenanceEntryModal } }) =>
  maintenanceEntryModal;

export const getDutyTimeModalEntryModal = ({ pages: { dutyTimeModal } }) => dutyTimeModal;

export const getReportsPage = ({ pages: { reports } }) => reports;

export const getNotifire = ({ common: { notifire } }) => notifire;

export const getDutyTimesPage = ({ pages: { dutyTimes } }) => dutyTimes;

export const getFlightTimesPage = ({ pages: { flightTimes } }) => flightTimes;

export const getCurrencyPage = ({ pages: { currency } }) => currency;

export const getCrewCompliancePage = ({ pages: { crewCompliance } }) => crewCompliance;

export const getDefaultAirports = ({ common: { defaultAirports } }) => defaultAirports;

export const getCommon = ({ common }) => common as CommonInitState;

export const getCompanyClients = state => getCommon(state).companyClients;
export const getIsDefaultCompanyClientsLoaded = state =>
  getCommon(state).isDefaultCompanyClientsLoaded;
export const getCompanyTags = state => getCommon(state).companyTags;

export const getCompanyUser = ({ user: { CompanyUsers = [] } }) => CompanyUsers?.[0];

export const getRouter = ({ router }) => router as RouterState;
